// src/components/artwork/ConservationDiagnostic.jsx
//
// MVP « Lieux & conservation » — mini-diagnostic bienveillant par œuvre.
// Réf. RECOMMANDATIONS_..._LIEUX_CONSERVATION §3, §4, §8, §10, §11, §12.
// Ton : carnet de soin, jamais culpabilisant. Aucune couleur seule ne porte le sens.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";
import { Leaf, ShieldCheck, AlertTriangle, Stethoscope, Info, RotateCcw } from "lucide-react";
import {
  CONSERVATION_QUESTIONS,
  CONSERVATION_MODE_META,
  CONSERVATION_MEDIUM_TIPS,
  CONSERVATION_STATUS,
  CONSERVATION_UI as UI,
  evaluateConservation,
  conservationMediumFamily,
  nextCheckDate,
  cText,
} from "../../utils/conservationDiagnostic";

const STATUS_ICON = {
  suitable: Leaf,
  improve: ShieldCheck,
  monitor: AlertTriangle,
  professional_advice: Stethoscope,
  incomplete: Info,
};

function StatusBadge({ status, lang }) {
  const s = CONSERVATION_STATUS[status] || CONSERVATION_STATUS.incomplete;
  const Icon = STATUS_ICON[status] || Info;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        fontSize: 13, fontWeight: 600, padding: "5px 12px", borderRadius: 999,
        color: s.fg, background: s.bg, border: `1px solid ${s.border}`,
      }}
    >
      <Icon size={15} strokeWidth={1.9} aria-hidden="true" />
      {cText(UI.status[status], lang)}
    </span>
  );
}

const CARD = { background: "#fffdf7", border: "1px solid #eadfc4", borderRadius: 16, padding: 20 };

export default function ConservationDiagnostic({ artworkId, clientId, mediumFamilyKey, locationId = null, readOnly = false }) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || "fr";

  const [lastCheck, setLastCheck] = useState(null);
  const [retaking, setRetaking] = useState(false);
  const [mode, setMode] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [reminderMonths, setReminderMonths] = useState(6); // rappel 3/6/12 mois, 0 = aucun

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!artworkId) return;
      const { data } = await supabase
        .from("conservation_checks")
        .select("*")
        .eq("artwork_id", artworkId)
        .order("checked_at", { ascending: false })
        .limit(1);
      if (!ignore) setLastCheck(data && data[0] ? data[0] : null);
    })();
    return () => { ignore = true; };
  }, [artworkId, savedAt]);

  const questions = mode ? CONSERVATION_QUESTIONS[mode] : [];
  const family = conservationMediumFamily(mediumFamilyKey);

  const startFlow = () => { setRetaking(true); setMode(null); setAnswers({}); setResult(null); };
  const pickMode = (m) => { setMode(m); setAnswers({}); setResult(null); };
  const setAnswer = (key, val) => setAnswers((prev) => ({ ...prev, [key]: val }));
  const compute = () => setResult(evaluateConservation({ mode, answers, mediumFamilyKey }));

  const save = async () => {
    if (!result || saving) return;
    setSaving(true);
    const next = reminderMonths ? nextCheckDate(reminderMonths) : null;
    const row = {
      client_id: clientId,
      artwork_id: artworkId,
      location_id: locationId,
      mode,
      medium_family: family,
      answers,
      status: result.status,
      positive_key: result.positive?.key || null,
      priority_key: result.priority?.key || null,
      action_key: result.priority?.key || null,
      why_key: result.priority?.key || null,
      medium_tip_key: result.mediumFamily || family,
      professional_advice: !!result.professionalAdvice,
      confidence: result.confidence,
      risk_count: result.riskCount,
      next_check_at: next,
    };
    const { error } = await supabase.from("conservation_checks").insert(row);
    setSaving(false);
    if (!error) {
      setSavedAt(new Date().toISOString());
      setRetaking(false);
      setMode(null);
      setResult(null);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" }) : "—");

  // En lecture seule (advisor/assureur) : rien à montrer s'il n'y a pas de bilan.
  if (readOnly && !lastCheck) return null;

  // ── Vue « dernier bilan » (compact) ──
  if ((lastCheck && !retaking) || (readOnly && lastCheck)) {
    const q = lastCheck.priority_key
      ? (CONSERVATION_QUESTIONS[lastCheck.mode] || []).find((x) => x.key === lastCheck.priority_key)
      : null;
    const isDue = lastCheck.next_check_at && new Date(lastCheck.next_check_at) <= new Date();
    return (
      <section style={{ ...CARD, marginTop: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ fontFamily: "Georgia, serif", fontSize: 18, margin: 0, color: "#1a1a1a" }}>
            {cText(UI.title, lang)}
          </h3>
          <StatusBadge status={lastCheck.status} lang={lang} />
        </div>
        {isDue && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#a85643", background: "#f7ece7", border: "1px solid #e6cdc2", borderRadius: 10, padding: "8px 12px" }}>
            <RotateCcw size={14} aria-hidden="true" />
            {cText(UI.dueNudge, lang)}
          </div>
        )}
        {q && (
          <p style={{ fontSize: 13.5, color: "#6b6459", lineHeight: 1.55, margin: "12px 0 0" }}>
            <strong style={{ color: "#1a1a1a" }}>{cText(UI.priorityHeading, lang)} — </strong>
            {cText(q.flag, lang)} {cText(q.action, lang)}
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginTop: 14 }}>
          <span style={{ fontSize: 12, color: "#8a8378" }}>
            {cText(UI.lastCheck, lang)} : {fmtDate(lastCheck.checked_at)}
            {lastCheck.next_check_at ? ` · ${cText(UI.nextCheck, lang)} : ${fmtDate(lastCheck.next_check_at)}` : ""}
          </span>
          {!readOnly && (
            <button
              type="button"
              onClick={startFlow}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#a9843f", background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              <RotateCcw size={14} aria-hidden="true" /> {cText(UI.retake, lang)}
            </button>
          )}
        </div>
        <p style={{ fontSize: 11, color: "#a49b8b", margin: "12px 0 0", fontStyle: "italic" }}>{cText(UI.disclaimer, lang)}</p>
      </section>
    );
  }

  // ── Vue diagnostic ──
  return (
    <section style={{ ...CARD, marginTop: 24 }}>
      <h3 style={{ fontFamily: "Georgia, serif", fontSize: 18, margin: 0, color: "#1a1a1a" }}>{cText(UI.title, lang)}</h3>
      <p style={{ fontSize: 13.5, color: "#6b6459", lineHeight: 1.55, margin: "6px 0 0" }}>{cText(UI.subtitle, lang)}</p>

      {/* Choix du mode (2 entrées éditoriales, doc §11) */}
      {!mode && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", marginBottom: 10 }}>{cText(UI.modeQuestion, lang)}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {["exposed", "stored"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => pickMode(m)}
                style={{ textAlign: "left", background: "#fff", border: "1px solid #eadfc4", borderRadius: 12, padding: 14, cursor: "pointer" }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>{cText(CONSERVATION_MODE_META[m].label, lang)}</div>
                <div style={{ fontSize: 12, color: "#8a8378", marginTop: 3, lineHeight: 1.4 }}>{cText(CONSERVATION_MODE_META[m].desc, lang)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Questions */}
      {mode && !result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gap: 14 }}>
            {questions.map((q) => (
              <div key={q.key}>
                <div style={{ fontSize: 13.5, color: "#1a1a1a", marginBottom: 6 }}>{cText(q.label, lang)}</div>
                <div style={{ display: "inline-flex", border: "1px solid #eadfc4", borderRadius: 10, overflow: "hidden" }}>
                  {["yes", "no", "unknown"].map((opt) => {
                    const active = answers[q.key] === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setAnswer(q.key, opt)}
                        style={{
                          fontSize: 12.5, padding: "6px 12px", border: "none", cursor: "pointer",
                          borderLeft: opt !== "yes" ? "1px solid #eadfc4" : "none",
                          background: active ? "#1a1a1a" : "#fff",
                          color: active ? "#fff" : "#6b6459",
                          fontWeight: active ? 600 : 400,
                        }}
                      >
                        {cText(UI.answer[opt], lang)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button
              type="button"
              onClick={compute}
              style={{ background: "#1a1a1a", color: "#fff", fontSize: 13, fontWeight: 500, padding: "10px 18px", borderRadius: 12, border: "none", cursor: "pointer" }}
            >
              {cText(UI.seeResult, lang)}
            </button>
            <button
              type="button"
              onClick={() => setMode(null)}
              style={{ background: "#fff", color: "#6b6459", fontSize: 13, padding: "10px 16px", borderRadius: 12, border: "1px solid #eadfc4", cursor: "pointer" }}
            >
              ←
            </button>
          </div>
        </div>
      )}

      {/* Restitution (doc §10 : point positif → priorité → action → pourquoi) */}
      {result && (
        <div style={{ marginTop: 18, background: "#fff", border: "1px solid #eadfc4", borderRadius: 14, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <StatusBadge status={result.status} lang={lang} />
          </div>

          {(result.status === "suitable" || result.status === "incomplete") && (
            <p style={{ fontSize: 13.5, color: "#6b6459", lineHeight: 1.55, margin: "12px 0 0" }}>
              {cText(UI.intro[result.status], lang)}
            </p>
          )}

          {result.positive && (
            <p style={{ fontSize: 13.5, color: "#1a1a1a", lineHeight: 1.55, margin: "12px 0 0" }}>
              <strong style={{ color: "#5b7052" }}>{cText(UI.positiveHeading, lang)} — </strong>
              {cText(result.positive.good, lang)}
            </p>
          )}

          {result.priority && (
            <div style={{ margin: "12px 0 0" }}>
              <p style={{ fontSize: 13.5, color: "#1a1a1a", lineHeight: 1.55, margin: 0 }}>
                <strong style={{ color: "#8a6d2f" }}>{cText(UI.priorityHeading, lang)} — </strong>
                {cText(result.priority.flag, lang)} {cText(result.priority.action, lang)}
              </p>
              <p style={{ fontSize: 12.5, color: "#8a8378", lineHeight: 1.5, margin: "5px 0 0" }}>
                {cText(UI.whyHeading, lang)} : {cText(result.priority.why, lang)}
              </p>
            </div>
          )}

          <p style={{ fontSize: 12.5, color: "#6b6459", lineHeight: 1.5, margin: "12px 0 0" }}>
            <strong>{cText(UI.mediumTipHeading, lang)} — </strong>
            {cText(CONSERVATION_MEDIUM_TIPS[result.mediumFamily] || CONSERVATION_MEDIUM_TIPS.unknown, lang)}
          </p>

          {result.professionalAdvice && (
            <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#f6eaec", border: "1px solid #e3c9cf" }}>
              <p style={{ fontSize: 12.5, color: "#8a3f4d", lineHeight: 1.5, margin: 0 }}>{cText(UI.professionalAdvice, lang)}</p>
            </div>
          )}

          <p style={{ fontSize: 11.5, color: "#a49b8b", margin: "14px 0 0", fontStyle: "italic" }}>
            {cText(UI.disclaimer, lang)} · {cText(UI.confidence[result.confidence], lang)}
          </p>

          {/* Rappel facultatif 3 / 6 / 12 mois (doc §8.7) */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            <span style={{ fontSize: 12.5, color: "#6b6459" }}>{cText(UI.remindLabel, lang)}</span>
            <div style={{ display: "inline-flex", border: "1px solid #eadfc4", borderRadius: 10, overflow: "hidden" }}>
              {[["m3", 3], ["m6", 6], ["m12", 12], ["none", 0]].map(([k, m], i) => {
                const active = reminderMonths === m;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setReminderMonths(m)}
                    style={{
                      fontSize: 12, padding: "5px 11px", border: "none", cursor: "pointer",
                      borderLeft: i > 0 ? "1px solid #eadfc4" : "none",
                      background: active ? "#1a1a1a" : "#fff",
                      color: active ? "#fff" : "#6b6459",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {cText(UI.remind[k], lang)}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{ background: "#1a1a1a", color: "#fff", fontSize: 13, fontWeight: 500, padding: "10px 18px", borderRadius: 12, border: "none", cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}
            >
              {saving ? cText(UI.saving, lang) : cText(UI.save, lang)}
            </button>
            <button
              type="button"
              onClick={() => setResult(null)}
              style={{ background: "#fff", color: "#6b6459", fontSize: 13, padding: "10px 16px", borderRadius: 12, border: "1px solid #eadfc4", cursor: "pointer" }}
            >
              {cText(UI.retake, lang)}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
